import React, { useMemo } from 'react';
import { Formik } from 'formik';
import { FiAlertCircle } from 'react-icons/fi';

import DeleteButton from 'components/common/button/DeleteButton';
import { FormGroup } from 'components/common/form';
import LFModal from 'components/common/LFModal';
import Loading from 'components/common/loading/Loading';

import { SKILLS_CATEGORY } from 'constants/permissions';

import { useUserPermissions } from 'hooks/query/useUserInfo';
import skillCategorySchema from 'schemas/skillCategorySchema';

import type { PermissionMap } from 'pages/settings/users/userPermissions/types';
import type { SkillCategoryFormValues } from '../types';

type Props = {
  skillCategory: SkillCategoryFormValues | null;
  handleSave: (skillCategory: SkillCategoryFormValues, id?: number | null) => void;
  handleDelete: (id: number) => void;
  handleCancel: () => void;
  saving: boolean;
  deleting: boolean;
  isHr?: boolean;
};

const getInitialSkillCategory = (skillCategory: SkillCategoryFormValues | null): SkillCategoryFormValues => ({
  id: skillCategory?.id,
  name: skillCategory?.name || '',
  description: skillCategory?.description || '',
});

const EditSkillCategory: React.FC<Props> = ({
  skillCategory,
  handleSave,
  handleDelete,
  handleCancel,
  saving,
  deleting,
}) => {
  const permissions = useUserPermissions() as PermissionMap;
  const selectedSkillCategory = useMemo(() => getInitialSkillCategory(skillCategory), [skillCategory]);

  return (
    <Formik<SkillCategoryFormValues>
      enableReinitialize
      initialValues={selectedSkillCategory}
      validationSchema={skillCategorySchema}
      onSubmit={(values) => handleSave(values, selectedSkillCategory.id ?? null)}
    >
      {({ values, handleSubmit, errors, touched, handleChange, handleBlur }) => (
        <form className="edit-skill card-form" onSubmit={handleSubmit}>
          <h3 className="edit-skill_header">Skill Category Details</h3>
          <FormGroup
            name="name"
            label="Skill Category Name"
            isMandatory={true}
            value={values.name}
            error={touched.name && errors.name ? errors.name : undefined}
            handleBlur={handleBlur}
            handleChange={handleChange}
            readOnly={!permissions[SKILLS_CATEGORY.CREATE]}
            placeholder="Skill Category Name"
          />
          <FormGroup
            name="description"
            label="Skill Description"
            isMandatory={false}
            multiline={true}
            value={values.description ?? ''}
            readOnly={!permissions[SKILLS_CATEGORY.CREATE]}
            error={touched.description && errors.description ? errors.description : undefined}
            handleBlur={handleBlur}
            handleChange={handleChange}
            placeholder="Skill Category Description"
          />
          <div className="mt-30 button-area">
            {(permissions[SKILLS_CATEGORY.CREATE] || permissions[SKILLS_CATEGORY.UPDATE]) && (
              <button
                type="submit"
                disabled={saving}
                className="btn btn--primary f-left card-button mr-10 min-width-80"
              >
                {saving ? <Loading /> : selectedSkillCategory.id ? 'Update' : 'Create'}
              </button>
            )}
            <button type="button" onClick={handleCancel} className="btn btn--outlined-grey f-left card-button">
              Cancel
            </button>
            {permissions[SKILLS_CATEGORY.DELETE] && selectedSkillCategory.id && (
              <LFModal
                TriggerBtn={DeleteButton}
                isLoading={deleting}
                modalStatusIcon={<FiAlertCircle color="#F44336" size={24} />}
                title={{ text: 'Do you want to delete this skill category?', type: 'danger' }}
                className="delete-modal"
                message={{ text: 'This action cannot be reverted back once deleted.', type: 'delete__text' }}
                renderFooter={true}
                buttons={[
                  {
                    text: 'Yes, delete',
                    type: 'red',
                    className: 'mr-12 delete__btn',
                    onClick: () => handleDelete(selectedSkillCategory.id ?? 0),
                    close: true,
                    showLoading: true,
                  },
                  {
                    text: 'No, go back',
                    type: 'outlined-grey',
                    className: 'py-8 px-12',
                    close: true,
                  },
                ]}
              />
            )}
          </div>
        </form>
      )}
    </Formik>
  );
};

export default EditSkillCategory;
